'use client';

import { useCallback, useRef, useState } from 'react';

/**
 * This code is copied and modified from the following source:
 * Author: uidotdev/usehooks https://usehooks.com/
 * Source: https://github.com/uidotdev/usehooks/blob/main/index.js#L753
 * License: MIT https://github.com/uidotdev/usehooks/blob/main/LICENSE
 */

interface DimensionsInterface {
  width: number | null;
  height: number | null;
}

type MeasureRefType = (node: Element | null) => void;

/**
 * Hook that measures DOM element dimensions using ResizeObserver.
 * Provides a ref callback and reactive dimensions that update when the element resizes.
 * Automatically handles observer cleanup and supports server-side rendering.
 *
 * @returns {[MeasureRefType, DimensionsInterface]} Tuple containing a ref callback to attach to an element and the current dimensions object.
 */
export function useMeasure(): [MeasureRefType, DimensionsInterface] {
  const [dimensions, setDimensions] = useState<DimensionsInterface>({
    width: null,
    height: null,
  });

  const previousObserver = useRef<ResizeObserver | null>(null);

  const customRef = useCallback((node: Element | null) => {
    if (previousObserver.current) {
      previousObserver.current.disconnect();
      previousObserver.current = null;
    }

    if (node?.nodeType === Node.ELEMENT_NODE) {
      const observer = new ResizeObserver(([entry]) => {
        if (entry && entry.borderBoxSize) {
          const { inlineSize: width, blockSize: height } = entry.borderBoxSize[0];

          setDimensions({ width, height });
        }
      });

      observer.observe(node);
      previousObserver.current = observer;
    }
  }, []);

  return [customRef, dimensions];
}
